/**
 * Vorsorge / AMV (/vorsorge, G-24…G-26): vier Tabs —
 * Fälligkeits-Cockpit (Filter + XLSX-Export als Blob + Mail-Versand),
 * Untersuchungen (Mitarbeiter-Records-CRUD, UNIQUE-409-Toast),
 * Katalog (Untersuchungs-CRUD) und Zuordnung Stelle↔Untersuchung.
 * Lesen: vorsorge_lesen; schreiben: personal_schreiben; löschen: loeschen.
 */

import { useMemo, useState } from "react"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Download, Mail, Pencil, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { EntityFormSheet, type FieldDef, type FormValues } from "@/components/admin/EntityFormSheet"
import { MasterDataSection, type Column } from "@/components/admin/MasterDataSection"
import { useMasterData } from "@/components/admin/useMasterData"
import { EXAMINATION_STATUS, EXAMINATION_STATUS_VALUES } from "@/components/examinations/labels"
import { DueBadge, LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { fmtDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { cn } from "@/lib/utils"
import { api, apiError } from "@/services/api"
import type {
  DueInWindow,
  Examination,
  ExaminationCreate,
  ExaminationRecord,
  PositionExamination,
  UntersuchungStatus,
} from "@/types"

// ---------------------------------------------------------------------------
// Gemeinsame Filter-Chip-Leiste
// ---------------------------------------------------------------------------

function ChipRow<T>({
  label,
  chips,
  value,
  onChange,
}: {
  label: string
  chips: { label: string; value: T }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex gap-1">
        {chips.map((chip) => (
          <button
            key={chip.label}
            type="button"
            onClick={() => onChange(chip.value)}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              value === chip.value
                ? "border-primary bg-primary text-primary-foreground"
                : "border-input bg-transparent text-muted-foreground hover:bg-accent"
            )}
          >
            {chip.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mail-Versand-Dialog (G-26)
// ---------------------------------------------------------------------------

function SendReportDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [raw, setRaw] = useState("")

  const send = useMutation({
    mutationFn: (recipients: string[]) => api.reports.sendExaminationsReport(recipients),
    onSuccess: (res) => {
      toast.success(`AMV-Report wird versendet an: ${res.recipients.join(", ")}`)
      setRaw("")
      onOpenChange(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const recipients = raw
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>AMV-Report per Mail versenden</DialogTitle>
          <DialogDescription>
            Der Report wird frisch erzeugt und an die angegebenen Empfänger geschickt
            (Komma, Semikolon oder Zeilenumbruch als Trenner).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label htmlFor="amv-recipients">Empfänger</Label>
          <Textarea
            id="amv-recipients"
            placeholder="name@firma.de, arbeitsmedizin@dienst.de"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            disabled={recipients.length === 0 || send.isPending}
            onClick={() => send.mutate(recipients)}
          >
            <Mail /> Versenden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Tab 1: Fälligkeits-Cockpit (G-26)
// ---------------------------------------------------------------------------

const DUE_IN_CHIPS: { label: string; value: DueInWindow | null }[] = [
  { label: "Heute", value: "today" },
  { label: "Monat", value: "month" },
  { label: "Quartal", value: "quarter" },
  { label: "Alle", value: null },
]

const DUE_DAYS_CHIPS: { label: string; value: number | null }[] = [
  { label: "3 Tage", value: 3 },
  { label: "7 Tage", value: 7 },
  { label: "10 Tage", value: 10 },
  { label: "14 Tage", value: 14 },
  { label: "—", value: null },
]

// Gemeinsame Grid-Spalten fuer Cockpit + Untersuchungen (header als String,
// damit die Spalten-Auswahl ihn anzeigen kann)
const RECORD_COLUMNS: ColumnDef<ExaminationRecord>[] = [
  {
    id: "employee",
    header: "Mitarbeiter",
    accessorFn: (r) => r.employeeName,
    meta: { cellClassName: "font-medium" },
  },
  {
    id: "examination",
    header: "Untersuchung",
    accessorFn: (r) => r.examinationName,
  },
  {
    id: "priority",
    header: "Priorität",
    accessorFn: (r) => r.priority ?? "",
    cell: ({ row }) => row.original.priority ?? "—",
    meta: { cellClassName: "text-muted-foreground" },
  },
  {
    id: "status",
    header: "Status",
    accessorFn: (r) => EXAMINATION_STATUS[r.status].label,
    cell: ({ row }) => (
      <Badge variant="outline" className={EXAMINATION_STATUS[row.original.status].badge}>
        {EXAMINATION_STATUS[row.original.status].label}
      </Badge>
    ),
  },
  {
    id: "examinedAt",
    // ISO-String als Accessor sortiert korrekt
    header: "Untersucht am",
    accessorFn: (r) => r.examinedAt ?? "",
    cell: ({ row }) => (row.original.examinedAt ? fmtDate(row.original.examinedAt) : "—"),
    meta: { cellClassName: "tabular-nums" },
  },
  {
    id: "dueDate",
    header: "Fällig am",
    accessorFn: (r) => r.dueDate ?? "",
    cell: ({ row }) => <DueBadge date={row.original.dueDate} />,
  },
]

function CockpitTab({ canWrite }: { canWrite: boolean }) {
  const [status, setStatus] = useState<UntersuchungStatus | "">("")
  const [employeeFilter, setEmployeeFilter] = useState("")
  const [examinationFilter, setExaminationFilter] = useState("")
  const [dueIn, setDueIn] = useState<DueInWindow | null>(null)
  const [dueWithinDays, setDueWithinDays] = useState<number | null>(null)
  const [mailOpen, setMailOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
  })
  const catalog = useQuery({
    queryKey: ["examinations"],
    queryFn: () => api.examinations.catalog.list({ size: 100 }),
  })

  const params = {
    size: 100,
    status: status || undefined,
    employee: employeeFilter ? Number(employeeFilter) : undefined,
    examination: examinationFilter ? Number(examinationFilter) : undefined,
    dueIn: dueIn ?? undefined,
    dueWithinDays: dueWithinDays ?? undefined,
  }
  const records = useQuery({
    queryKey: ["examination-records", "cockpit", params],
    queryFn: () => api.examinations.records.list(params),
    placeholderData: keepPreviousData,
  })

  // XLSX-Export mit denselben Filtern — Blob-Download (Header-Auth!)
  async function downloadXlsx() {
    setDownloading(true)
    try {
      await api.reports.downloadExaminationsXlsx({
        status: status || undefined,
        employeeId: employeeFilter ? Number(employeeFilter) : undefined,
        examinationId: examinationFilter ? Number(examinationFilter) : undefined,
        dueWithinDays: dueWithinDays ?? undefined,
      })
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setDownloading(false)
    }
  }

  const rows = records.data?.data ?? []

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44 space-y-1.5">
            <Label>Status</Label>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as UntersuchungStatus | "")}
            >
              <option value="">Alle Status</option>
              {EXAMINATION_STATUS_VALUES.map((s) => (
                <option key={s} value={s}>
                  {EXAMINATION_STATUS[s].label}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-52 space-y-1.5">
            <Label>Mitarbeiter</Label>
            <Select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}>
              <option value="">Alle Mitarbeiter</option>
              {(employees.data?.data ?? []).map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.displayName}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-56 space-y-1.5">
            <Label>Untersuchung</Label>
            <Select
              value={examinationFilter}
              onChange={(e) => setExaminationFilter(e.target.value)}
            >
              <option value="">Alle Untersuchungen</option>
              {(catalog.data?.data ?? []).map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.name}
                </option>
              ))}
            </Select>
          </div>
          <ChipRow label="Fällig in" chips={DUE_IN_CHIPS} value={dueIn} onChange={setDueIn} />
          <ChipRow
            label="Fällig ≤ Tage"
            chips={DUE_DAYS_CHIPS}
            value={dueWithinDays}
            onChange={setDueWithinDays}
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadXlsx} disabled={downloading}>
            <Download /> XLSX
          </Button>
          {canWrite && (
            <Button variant="outline" onClick={() => setMailOpen(true)}>
              <Mail /> Per Mail senden
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="py-2">
          {records.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={RECORD_COLUMNS}
              data={rows}
              storageKey="examinations-cockpit"
              emptyMessage="Keine Untersuchungen für die gewählten Filter."
            />
          )}
        </CardContent>
      </Card>

      <SendReportDialog open={mailOpen} onOpenChange={setMailOpen} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 2: Mitarbeiter-Untersuchungen (Records-CRUD, G-25)
// ---------------------------------------------------------------------------

function RecordsTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const queryClient = useQueryClient()
  const [employeeFilter, setEmployeeFilter] = useState("")
  const [dialog, setDialog] = useState<{ open: boolean; row: ExaminationRecord | null }>({
    open: false,
    row: null,
  })

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
  })
  const catalog = useQuery({
    queryKey: ["examinations"],
    queryFn: () => api.examinations.catalog.list({ size: 100 }),
  })

  const params = {
    size: 100,
    employee: employeeFilter ? Number(employeeFilter) : undefined,
  }
  const records = useQuery({
    queryKey: ["examination-records", params],
    queryFn: () => api.examinations.records.list(params),
    placeholderData: keepPreviousData,
  })

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["examination-records"] })
  }

  const save = useMutation({
    mutationFn: ({ id, values }: { id: string | null; values: FormValues }) => {
      const base = {
        examinationId: Number(values.examinationId),
        priority: String(values.priority).trim() || null,
        status: values.status as UntersuchungStatus,
        examinedAt: String(values.examinedAt) || null,
        dueDate: String(values.dueDate) || null,
      }
      return id
        ? api.examinations.records.update(id, base)
        : api.examinations.records.create({ ...base, employeeId: Number(values.employeeId) })
    },
    onSuccess: (_data, vars) => {
      invalidate()
      toast.success(vars.id ? "Untersuchung aktualisiert" : "Untersuchung angelegt")
      setDialog({ open: false, row: null })
    },
    // UNIQUE (Mitarbeiter, Untersuchung) → 409 mit deutscher Meldung (G-25)
    onError: (e) => toast.error(apiError(e)),
  })

  const del = useMutation({
    mutationFn: (id: string) => api.examinations.records.remove(id),
    onSuccess: () => {
      invalidate()
      toast.success("Untersuchung gelöscht")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const rows = records.data?.data ?? []
  const editing = dialog.row

  // Basis-Spalten + Aktions-Spalte (nicht sortier-/ausblendbar)
  const columns = useMemo<ColumnDef<ExaminationRecord>[]>(
    () => [
      ...RECORD_COLUMNS,
      ...(canWrite || canDelete
        ? ([
            {
              id: "actions",
              header: "Aktionen",
              enableSorting: false,
              enableHiding: false,
              meta: { headClassName: "w-24 text-right", cellClassName: "text-right" },
              cell: ({ row }) => (
                <div className="flex justify-end gap-1">
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7"
                      aria-label="Bearbeiten"
                      onClick={() => setDialog({ open: true, row: row.original })}
                    >
                      <Pencil className="!size-3.5" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-7 text-destructive hover:text-destructive"
                      aria-label="Löschen"
                      disabled={del.isPending}
                      onClick={() => {
                        if (window.confirm("Untersuchung wirklich löschen?"))
                          del.mutate(row.original.id)
                      }}
                    >
                      <Trash2 className="!size-3.5" />
                    </Button>
                  )}
                </div>
              ),
            },
          ] satisfies ColumnDef<ExaminationRecord>[])
        : []),
    ],
    // del ist eine stabile Mutation-Referenz
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite, canDelete, del.isPending],
  )

  const fields: FieldDef[] = [
    ...(editing
      ? []
      : ([
          {
            key: "employeeId",
            label: "Mitarbeiter",
            kind: "select",
            required: true,
            emptyOption: "— wählen —",
            options: (employees.data?.data ?? []).map((e) => ({
              value: e.id,
              label: e.displayName,
            })),
          },
        ] satisfies FieldDef[])),
    {
      key: "examinationId",
      label: "Untersuchung",
      kind: "select",
      required: true,
      emptyOption: "— wählen —",
      options: (catalog.data?.data ?? []).map((ex) => ({ value: ex.id, label: ex.name })),
    },
    { key: "priority", label: "Priorität", kind: "text" },
    {
      key: "status",
      label: "Status",
      kind: "select",
      required: true,
      options: EXAMINATION_STATUS_VALUES.map((s) => ({
        value: s,
        label: EXAMINATION_STATUS[s].label,
      })),
    },
    { key: "examinedAt", label: "Untersucht am", kind: "date" },
    { key: "dueDate", label: "Fällig am", kind: "date" },
  ]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="w-56 space-y-1.5">
          <Label>Mitarbeiter</Label>
          <Select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}>
            <option value="">Alle Mitarbeiter</option>
            {(employees.data?.data ?? []).map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.displayName}
              </option>
            ))}
          </Select>
        </div>
        {canWrite && (
          <Button onClick={() => setDialog({ open: true, row: null })}>
            <Plus /> Neue Untersuchung
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="py-2">
          {records.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="examinations-records"
              emptyMessage="Keine Untersuchungen erfasst."
            />
          )}
        </CardContent>
      </Card>

      <EntityFormSheet
        sheetKey="entity-form-examinations"
        open={dialog.open}
        onOpenChange={(open) => setDialog((s) => ({ ...s, open }))}
        title={
          editing ? `Untersuchung bearbeiten — ${editing.employeeName}` : "Neue Untersuchung"
        }
        fields={fields}
        initialValues={{
          employeeId: editing?.employeeId ?? "",
          examinationId: editing?.examinationId ?? "",
          priority: editing?.priority ?? "",
          status: editing?.status ?? "AUSSTEHEND",
          examinedAt: editing?.examinedAt ?? "",
          dueDate: editing?.dueDate ?? "",
        }}
        pending={save.isPending}
        onSubmit={(values) => save.mutate({ id: editing?.id ?? null, values })}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 3: Untersuchungskatalog
// ---------------------------------------------------------------------------

function CatalogTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const crud = useMasterData<Examination, ExaminationCreate>(
    "examinations",
    api.examinations.catalog,
    "Untersuchung"
  )

  const columns: Column<Examination>[] = [
    { header: "Name", cell: (e) => <span className="font-medium">{e.name}</span> },
    { header: "Beschreibung", cell: (e) => e.description ?? "—" },
  ]

  const fields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true, placeholder: "z. B. G 20 Lärm" },
    { key: "description", label: "Beschreibung", kind: "textarea" },
  ]

  return (
    <>
      <MasterDataSection
        isLoading={crud.isLoading}
        rows={crud.rows}
        columns={columns}
        storageKey="masterdata-untersuchungen"
        emptyText="Keine Untersuchungen im Katalog."
        canWrite={canWrite}
        canDelete={canDelete}
        createLabel="Neue Untersuchung"
        onCreate={() => crud.setDialog({ open: true, row: null })}
        onEdit={(row) => crud.setDialog({ open: true, row })}
        onDelete={crud.confirmDelete}
        deletePending={crud.del.isPending}
      />
      <EntityFormSheet
        sheetKey="entity-form-examinations"
        open={crud.dialog.open}
        onOpenChange={(open) => crud.setDialog((s) => ({ ...s, open }))}
        title={crud.dialog.row ? "Untersuchung bearbeiten" : "Neue Untersuchung"}
        fields={fields}
        initialValues={{
          name: crud.dialog.row?.name ?? "",
          description: crud.dialog.row?.description ?? "",
        }}
        pending={crud.save.isPending}
        onSubmit={(v) =>
          crud.save.mutate({
            id: crud.dialog.row?.id ?? null,
            body: {
              name: String(v.name).trim(),
              description: String(v.description).trim() || null,
            },
          })
        }
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Tab 4: Zuordnung Stelle↔Untersuchung (position_examinations, G-24)
// ---------------------------------------------------------------------------

function MappingTab({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient()
  const [positionFilter, setPositionFilter] = useState("")
  const [addOpen, setAddOpen] = useState(false)

  const positions = useQuery({
    queryKey: ["positions"],
    queryFn: () => api.organization.positions.list({ size: 100 }),
  })
  const catalog = useQuery({
    queryKey: ["examinations"],
    queryFn: () => api.examinations.catalog.list({ size: 100 }),
  })

  const params = {
    size: 100,
    position: positionFilter ? Number(positionFilter) : undefined,
  }
  const links = useQuery({
    queryKey: ["position-examinations", params],
    queryFn: () => api.examinations.positionExaminations.list(params),
    placeholderData: keepPreviousData,
  })

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["position-examinations"] })
  }

  const add = useMutation({
    mutationFn: (values: FormValues) =>
      api.examinations.positionExaminations.create({
        positionId: Number(values.positionId),
        examinationId: Number(values.examinationId),
        active: values.active === true,
      }),
    onSuccess: () => {
      invalidate()
      toast.success("Zuordnung angelegt")
      setAddOpen(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.examinations.positionExaminations.update(id, { active }),
    onSuccess: () => invalidate(),
    onError: (e) => toast.error(apiError(e)),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.examinations.positionExaminations.remove(id),
    onSuccess: () => {
      invalidate()
      toast.success("Zuordnung entfernt")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const rows = links.data?.data ?? []

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<PositionExamination>[]>(
    () => [
      {
        id: "position",
        header: "Stelle",
        accessorFn: (link) => link.positionName,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "examination",
        header: "Untersuchung",
        accessorFn: (link) => link.examinationName,
      },
      {
        id: "active",
        header: "Aktiv",
        // Boolescher Wert als Zahl → sortierbar
        accessorFn: (link) => (link.active ? 1 : 0),
        cell: ({ row }) => (
          <Switch
            checked={row.original.active}
            disabled={!canWrite || toggleActive.isPending}
            onCheckedChange={(active) =>
              toggleActive.mutate({ id: row.original.id, active })
            }
            aria-label="Zuordnung aktiv"
          />
        ),
      },
      ...(canWrite
        ? ([
            {
              id: "actions",
              header: "Aktionen",
              enableSorting: false,
              enableHiding: false,
              meta: { headClassName: "w-20 text-right", cellClassName: "text-right" },
              cell: ({ row }) => (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-destructive hover:text-destructive"
                  aria-label="Entfernen"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm("Zuordnung wirklich entfernen?"))
                      remove.mutate(row.original.id)
                  }}
                >
                  <Trash2 className="!size-3.5" />
                </Button>
              ),
            },
          ] satisfies ColumnDef<PositionExamination>[])
        : []),
    ],
    // toggleActive/remove sind stabile Mutation-Referenzen
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite, toggleActive.isPending, remove.isPending],
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="w-64 space-y-1.5">
          <Label>Stelle</Label>
          <Select value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)}>
            <option value="">Alle Stellen</option>
            {(positions.data?.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
        {canWrite && (
          <Button onClick={() => setAddOpen(true)}>
            <Plus /> Neue Zuordnung
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="py-2">
          {links.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="examinations-zuordnung"
              emptyMessage="Keine Zuordnungen vorhanden."
            />
          )}
        </CardContent>
      </Card>

      <EntityFormSheet
        sheetKey="entity-form-examinations"
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Neue Zuordnung Stelle ↔ Untersuchung"
        fields={[
          {
            key: "positionId",
            label: "Stelle",
            kind: "select",
            required: true,
            emptyOption: "— wählen —",
            options: (positions.data?.data ?? []).map((p) => ({ value: p.id, label: p.name })),
          },
          {
            key: "examinationId",
            label: "Untersuchung",
            kind: "select",
            required: true,
            emptyOption: "— wählen —",
            options: (catalog.data?.data ?? []).map((ex) => ({ value: ex.id, label: ex.name })),
          },
          { key: "active", label: "Aktiv", kind: "switch" },
        ]}
        initialValues={{ positionId: "", examinationId: "", active: true }}
        pending={add.isPending}
        onSubmit={(values) => add.mutate(values)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

export function ExaminationsPage() {
  const canWrite = useCan("personal_schreiben")
  const canDelete = useCan("loeschen")
  const [tab, setTab] = useState("cockpit")

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Vorsorge (AMV)</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="cockpit">Fälligkeits-Cockpit</TabsTrigger>
          <TabsTrigger value="untersuchungen">Untersuchungen</TabsTrigger>
          <TabsTrigger value="katalog">Katalog</TabsTrigger>
          <TabsTrigger value="zuordnung">Zuordnung Stelle ↔ Untersuchung</TabsTrigger>
        </TabsList>
        <TabsContent value="cockpit">
          <CockpitTab canWrite={canWrite} />
        </TabsContent>
        <TabsContent value="untersuchungen">
          <RecordsTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="katalog">
          <CatalogTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="zuordnung">
          <MappingTab canWrite={canWrite} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
